'use client';

import { useMemo, useState } from 'react';
import {
  ArticleCard,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
} from '@highereducation/hen-ui';
import { formatDate } from '@hen-site/utils/formatDate';
import type { ContributorPost } from '@hen-site/types/contributor';

const PAGE_SIZE = 4;

function humanizeContributionType(raw: string): string | undefined {
  const contributionType = raw.trim();
  if (!contributionType) return undefined;
  const normalized = contributionType.toLowerCase();
  if (normalized.includes('review')) return 'Reviewer';
  if (normalized.includes('edit')) return 'Editor';
  if (normalized.includes('writ') || normalized.includes('author'))
    return 'Writer';
  return contributionType.charAt(0).toUpperCase() + contributionType.slice(1);
}

interface ContributorProfileArticleSectionProps {
  posts: ContributorPost[];
  profileName: string;
}

export default function ContributorProfileArticleSection({
  posts,
  profileName,
}: ContributorProfileArticleSectionProps) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const heading = `Articles by ${profileName}`;

  const visible = useMemo(
    () => posts.slice(0, visibleCount),
    [posts, visibleCount],
  );

  const hasMore = posts.length > visibleCount;

  return (
    <section aria-label={heading}>
      <Flex direction='column' gap='xl' className='w-full'>
        <Flex direction='column' gap='xxs'>
          <Heading variant='h2'>{heading}</Heading>
          <Text variant='b4' color='subtext'>
            Browse recent articles associated with this contributor.
          </Text>
        </Flex>
        <Grid columns={1} columns-lg={2} gap='md' asChild>
          <ul role='list'>
            {visible.map((post) => (
              <li
                key={post.id}
                role='listitem'
                className='[&>*]:h-full [&>*>a]:h-full [&>*>a>*]:h-full'
              >
                <ArticleCard
                  title={post.title}
                  brow={
                    post.contributionType
                      ? humanizeContributionType(post.contributionType)
                      : undefined
                  }
                  author={profileName}
                  date={formatDate(post.date) ?? undefined}
                  href={post.url}
                  size='sm'
                  border='stroke'
                />
              </li>
            ))}
          </ul>
        </Grid>
        {hasMore ? (
          <Flex direction='row'>
            <Button
              type='button'
              variant='blackOutline'
              size='sm'
              className='rounded-full px-md'
              iconAfter='arrow-down'
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            >
              Load More
            </Button>
          </Flex>
        ) : null}
      </Flex>
    </section>
  );
}
